import { JetView } from 'webix-jet'; // Importing JetView from webix-jet
import { getCities } from 'models/cities'; // Importing function to get cities
import { getTags } from 'models/tags'; // Importing function to get tags
import { getPositions } from 'models/positions'; // Importing function to get positions
import 'webix/photo'; // Importing photo component from webix
import 'webix/tinymce/tinymce'; // Importing TinyMCE editor from webix
import { devConfig } from 'config/config'; // Importing development configuration

/**
 * InformationView class extending JetView to create a form for displaying and editing user information.
 */
export default class InformationView extends JetView {
    /**
     * Configures the UI components of the view.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const screen = this.app.config.size; // Screen size configuration

        // Main information form section
        const main_info = {
            margin: 10,
            rows: [
                {
                    view: 'text', // Text input for ID
                    name: 'Id',
                    label: _("<span class='icon-id'></span> ID"),
                    labelPosition: 'top',
                    placeholder: _('Id'),
                    invalidMessage: _('A ID is required'),
                    disabled: true, // Disabled field
                },
                // Additional text inputs for personal information
                {
                    view: 'text',
                    name: 'FirstName',
                    label: _('Primer Nombre'),
                    labelPosition: 'top',
                    placeholder: _('Primer Nombre'),
                    invalidMessage: _('A name is required'),
                    disabled: true,
                },
                {
                    view: 'text',
                    name: 'MiddleName',
                    label: _('Segundo Nombre'),
                    labelPosition: 'top',
                    placeholder: _('Segundo Nombre'),
                    invalidMessage: _('A name is required'),
                    disabled: true,
                },
                {
                    view: 'text',
                    name: 'LastName',
                    label: _('Apellido'),
                    labelPosition: 'top',
                    placeholder: _('Apellido'),
                    disabled: true,
                },
                {
                    view: 'text',
                    name: 'Currency',
                    label: "<span class='webix_icon mdi mdi-coin'></span>" + _('Moneda'),
                    labelPosition: 'top',
                    placeholder: _('Moneda'),
                    disabled: true,
                },
                {
                    view: 'text',
                    name: 'Email',
                    label: _('Email'),
                    labelPosition: 'top',
                    placeholder: _('Email'),
                    disabled: true,
                },
                {
                    view: 'text',
                    name: 'CreatedLocalDate',
                    label: _('Fecha de registro'),
                    labelPosition: 'top',
                    placeholder: _('Fecha de registro'),
                    disabled: true,
                },
            ],
        };

        // Notifications radio button section
        const notifications = {
            view: 'radio',
            name: 'notifications',
            label: _('Notificaciones'),
            labelPosition: 'top',
            value: 1, // Default value
            options: [
                { id: 1, value: _('Si') }, // Yes option
                { id: 2, value: _('No') }, // No option
            ],
        };

        // Left main section configuration
        const left_main = {
            gravity: 3,
            minWidth: 200,
            margin: 10,
            rows: [main_info],
        };

        // More information section
        const more_info = {
            gravity: 3,
            minWidth: 200,
            margin: 10,
            rows: [
                {
                    view: 'richselect', // Rich select for country
                    name: 'CountryId',
                    id: 'country',
                    localId: 'country:combo',
                    label: _('País Residencia'),
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '),
                    options: [],
                    disabled: true,
                },
                // Additional rich selects for region and city
                {
                    view: 'richselect',
                    name: 'RegionId',
                    id: 'ProvinceRegion',
                    localId: 'ProvinceRegion:combo',
                    label: _('Provincia/Región Residencia'),
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '),
                    options: [],
                    disabled: true,
                },
                {
                    view: 'richselect',
                    name: 'CityId',
                    id: 'City',
                    localId: 'cities:combo',
                    label: _('Ciudad Residencia'),
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '),
                    options: [],
                    disabled: true,
                },
                {
                    view: 'text',
                    name: 'Address',
                    label: _('Dirección'),
                    labelPosition: 'top',
                    placeholder: _('Dirección'),
                    disabled: true,
                },
                {
                    view: 'text',
                    name: 'ZipCode',
                    label: _('Código Zip'),
                    labelPosition: 'top',
                    placeholder: _('Código Zip'),
                    disabled: true,
                },
                {
                    view: 'text',
                    name: 'Phone',
                    label: "<span class='icon-phone1'></span>" + _('Teléfono'),
                    labelPosition: 'top',
                    placeholder: _('Teléfono'),
                    disabled: true,
                },
                {
                    view: 'text',
                    name: 'MobilePhone',
                    label: _('Celular'),
                    labelPosition: 'top',
                    placeholder: _('Celular'),
                    disabled: true,
                },
            ],
        };

        // Right photo section
        const right_photo = {
            gravity: 3,
            margin: 10,
            rows: [
                {
                    view: 'photo', // Photo upload component
                    name: 'photo',
                    css: 'form_photo',
                    width: 260,
                    height: 260,
                    borderless: true,
                },
                {
                    view: 'multicombo', // Multi-select for tags
                    name: 'tags',
                    localId: 'tags:combo',
                    placeholder: _('Click to add tags'),
                    options: [],
                },
            ],
        };

        // Upper section configuration
        const upper_section = {
            multiview: true,
            padding: 10,
            cols: [left_main, { gravity: 0, minWidth: 20 }],
        };

        // Narrow upper section for small screens
        const upper_section_narrow = {
            cols: [
                {
                    gravity: 4,
                    margin: 10,
                    rows: [main_info],
                },
            ],
        };

        // Button section for saving the form
        const buttons = {
            margin: 10,
            cols: [
                {},
                {
                    view: 'button',
                    value: _('Guardar'), // Save button
                    type: 'form',
                    autowidth: true,
                    click: () => {
                        if (this.getRoot().validate()) {
                            // Validate form
                            const newdata = this.getRoot().getValues(); // Get form values
                            this.app.callEvent('customer:save', [newdata]); // Trigger save event
                        }
                    },
                },
            ],
        };

        webix.ui.fullScreen(); // Set UI to full screen mode
        return {
            view: 'form', // Main form view
            rows: [
                screen !== 'small' ? upper_section : upper_section_narrow, // Conditional rendering based on screen size
                buttons,
            ],
            rules: {
                fname: webix.rules.isNotEmpty, // Validation rule for non-empty fields
            },
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     * @param {Object} form - The form instance.
     */
    init(form) {
        var vthis = this; // Reference to the current instance
        this.on(this.app, 'form:update2', (id) => {
            // Event listener for updating form
            webix.ajax().get(
                devConfig.geturlApi() + 'Client/GetClientById', // API call to get client data
                'id=' + id,
                {
                    error: function (text, data, XmlHttpRequest) {
                        // Error handling
                        webix.message('Error! ');
                    },
                    success: function (text, data, XmlHttpRequest) {
                        // Success handling
                        text = JSON.parse(text); // Parse response
                        vthis.app.callEvent('customer:updatedata', [
                            text[0], // Trigger update event with client data
                        ]);
                        $$('country').setValue(text[0].CountryId); // Set country value
                        webix.delay(
                            function () {
                                $$('ProvinceRegion').setValue(
                                    text[0].RegionId, // Set region value
                                );
                                webix.delay(
                                    function () {
                                        $$('City').setValue(text[0].CityId); // Set city value
                                    },
                                    '',
                                    '',
                                    1000, // Delay for setting city value
                                );
                            },
                            '',
                            '',
                            1000, // Delay for setting region value
                        );
                    },
                },
            );
        });
        this.on(
            this.app,
            'customer:updatedata',
            (
                person, // Event listener for updating form values
            ) => form.setValues(person),
        );
        this.on(this.app, 'person:select', (person) => form.setValues(person)); // Event listener for person selection
        vthis.app.callEvent('form:update2', [vthis.getParam('user', true)]); // Trigger initial update event
    }

    /**
     * Retrieves localized options for combo boxes and populates them.
     */
    getLocalizedComboOptions() {
        const _ = this.app.getService('locale')._; // Localization service
        let p_options = webix.copy(getPositions()); // Get positions options
        p_options.map((x) => (x.value = _(x.value))); // Localize position values
        this.$$('position:combo').getPopup().getList().parse(p_options); // Populate positions combo

        let t_options = webix.copy(getTags()); // Get tags options
        t_options.map((x) => (x.value = _(x.value))); // Localize tag values
        this.$$('tags:combo').getPopup().getList().parse(t_options); // Populate tags combo

        let c_options = webix.copy(getCities()); // Get cities options
        c_options.map((x) => (x.value = _(x.value))); // Localize city values
        this.$$('cities:combo').getPopup().getList().parse(c_options); // Populate cities combo
    }
}

// FILE DOCUMENTED
